/*
 * IndexedDB `teambase`, version 1: one database, one registration list, one upgrade function
 * (spec 12 §2.2, architecture.md §6). Every store of the model is declared here — core's own and
 * whatever a frontend module registers through its `idbStores` — so the upgrade path stays one
 * function even though two owners write into it.
 *
 * The never-lose-work layer is not in this database. Drafts and the outbox live in `teambase-drafts`
 * (core/drafts/db.ts), because what this database holds is a cache that a user or version mismatch
 * deletes, and typed text must survive exactly that (A19, ruled 2026-09-07 on W3-10b OPEN 1).
 *
 * Guarded, never throwing (REUSE J4): a browser that refuses site data, a private window, a quota
 * error at open — each answers `undefined`, and the caller runs a memory-only model with the
 * banner of spec 08. Every later access goes through the handle this returns.
 */

import { deleteDB, openDB, type IDBPDatabase } from "idb"
import type { IdbStoreDef } from "../modules/registry.ts"

export const DB_NAME = "teambase"

/** Bumped when a store or index is added; the upgrade function creates whatever is missing. */
export const DB_VERSION = 1

export type Db = IDBPDatabase

/**
 * The model's own stores. `meta` holds one row per key (`lastEventId`, `userId`, …), the others
 * one row per entity; `messages` is keyed by conversation and seq so a window reads back in order.
 * These are what a version mismatch or a revocation clears.
 */
export const MODEL_STORES: readonly IdbStoreDef[] = [
	{ name: "meta", keyPath: "k" },
	{ name: "users", keyPath: "id" },
	{ name: "groups", keyPath: "id" },
	{ name: "conversations", keyPath: "id" },
	{ name: "messages", keyPath: ["conv", "seq"], indexes: [{ name: "by_conv", keyPath: "conv" }] },
	{ name: "modules", keyPath: "id" },
]

/**
 * The bytes of an attachment picked while offline (spec 08 §2.4). It sits with the model rather
 * than with the drafts, because these bytes are re-fetchable and a wipe may take them.
 */
export const LOCAL_STORES: readonly IdbStoreDef[] = [{ name: "blobs", keyPath: "localBlobKey" }]

export const CORE_STORES: readonly IdbStoreDef[] = [...MODEL_STORES, ...LOCAL_STORES]

/** The one upgrade function: creates every declared store and index that does not exist yet. */
function upgrade(db: IDBPDatabase, stores: readonly IdbStoreDef[]): void {
	for (const def of stores) {
		if (db.objectStoreNames.contains(def.name)) continue
		const store = db.createObjectStore(def.name, { keyPath: def.keyPath })
		for (const index of def.indexes ?? []) {
			store.createIndex(index.name, index.keyPath, { unique: index.unique ?? false })
		}
	}
}

/**
 * Opens the database with core's stores plus the modules' own. `undefined` means this browser
 * keeps nothing: the caller carries on in memory. A connection that another tab wants to upgrade
 * closes itself so the upgrade can proceed; the next open reconnects.
 */
export async function openDb(moduleStores: readonly IdbStoreDef[] = []): Promise<Db | undefined> {
	if (typeof indexedDB === "undefined") return undefined
	const stores = [...CORE_STORES, ...moduleStores]
	try {
		return await openDB(DB_NAME, DB_VERSION, {
			upgrade(db) {
				upgrade(db, stores)
			},
			blocking(_current, _blocked, event) {
				;(event.target as IDBDatabase | null)?.close()
			},
		})
	} catch {
		return undefined
	}
}

/** Removes the whole model database — the sign-out sweep, not the wipe that keeps a window. */
export async function deleteDb(): Promise<void> {
	if (typeof indexedDB === "undefined") return
	try {
		await deleteDB(DB_NAME)
	} catch {
		// Nothing to delete in a browser that refuses site data.
	}
}
